/* eslint-disable compat/compat */
/* document.fonts is supported in Edge 18, not sure why this lint error is happening
   https://developer.mozilla.org/en-US/docs/Web/API/Document/fonts
*/

import { getTheme } from 'foundations-theming/utils/getTheme';
import CanvasLightTheme from 'foundations-theming/themes/canvas/light/theme';
let fontsHaveLoaded = false;
let forceSkip = false;

/**
 * @internal
 * FOR TESTS ONLY: Set the fonts have loaded flag to a given value.
 * @param {boolean} value - The value to set the fonts have loaded flag to.
 */
export const setFontsHaveLoaded = value => {
  fontsHaveLoaded = value;
};

/**
 * Hook for tests to skip font measurement. Must be called before `DOMContentLoaded` fires.
 */
export const forceSkipFontMeasurement = () => {
  forceSkip = true;
};

/**
 * Get all typography keys from any of the themes
 * @returns {Array<string>}
 */
const getTypographyKeysFromTheme = () => {
  // getting all typography keys from any of the themes
  const theme = getTheme({
    theme: CanvasLightTheme
  });
  const typography = theme.typography;
  return Object.keys(typography);
};

/**
 * Checks for font loading status and loads any fonts that are not loaded
 * @param {string[]} fontFamilyCSSVariables - The CSS variables for the font families to check
 * @returns {boolean} - Whether all fonts are loaded
 */
const checkFonts = fontFamilyCSSVariables => {
  const stylesheet = getComputedStyle(document.documentElement);

  // get all font family values from the theme css variables, deduped
  const fontFamilyValuesSet = new Set(fontFamilyCSSVariables.map(variable => {
    return stylesheet.getPropertyValue(variable);
  }));

  // convert all font lists to a single array of font names
  const allFonts = Array.from(fontFamilyValuesSet).join(',').split(',');

  // get all loaded or unloaded fonts in the document and filter if they match the theme fonts
  const matchingDocumentFonts = [...document.fonts.values()].filter(font => allFonts.includes(font.family));

  // check if all theme matching document fonts are loaded
  const allFontsLoaded = matchingDocumentFonts.length && matchingDocumentFonts.every(font => {
    if (font.status === 'loaded') {
      return true;
    }
    // attempt to load the font if it is not loaded so that it's available for the next check.
    // This is necessary for some lazy loaded fonts i.e. code monospace
    // Can be marked void since we are polling and don't need the result in this check.
    void font.load();
    return false;
  });
  if (allFontsLoaded) {
    fontsHaveLoaded = true;
    return true;
  }
  return false;
};

/**
 * A function that resolves when requested fonts have been loaded.
 * Note that any requested fonts will NOT load lazily, they will be forced to load immediately.
 * You can check only specific fonts by passing in an array of typography keys.
 * Which can improve the performance of the check.
 * @param {string[]} keysToCheck - The typography keys to check for. If not provided, all theme fonts will be checked.
 * @returns {Promise<void>} - A promise that resolves when all requested fonts are loaded.
 */
export const checkFontsLoaded = keysToCheck => {
  if (forceSkip) {
    return Promise.resolve();
  }
  const typographyKeys = getTypographyKeysFromTheme();
  const keysToCheckValidated = (keysToCheck === null || keysToCheck === void 0 ? void 0 : keysToCheck.filter(key => typographyKeys.includes(key))) || typographyKeys;

  // convert typography keys to font family css variables
  const fontFamilyCSSVariables = keysToCheckValidated.map(key => {
    return `--trellis-typography-${key}-font-family`;
  });
  return new Promise(resolve => {
    const poll = () => {
      if (checkFonts(fontFamilyCSSVariables)) {
        resolve();
      } else {
        setTimeout(poll, 50);
      }
    };
    poll();
  });
};

/**
 * A function that corresponds to whether `fontsLoadedPromise` has resolved.
 * @returns {boolean}
 */
export const fontsLoaded = () => fontsHaveLoaded;